<!-- @format -->

<template>
    <div class="gst-hotels-map__wrapper d-flex flex-column">
        <template v-if="!resultsNotFound">
            <div class="d-flex align-items-center justify-center flex-row">
                <BaseButton
                    v-show="showReset"
                    outlined
                    class="gst-hotels-map__refresh-button u-mouse-pointer"
                    alt="Reset map to initial search"
                    @click="resetToInitialSearch()">
                    <BaseIcon symbol-id="icons--reset" />
                </BaseButton>
                <template v-if="!showDifferentCountryNotification">
                    <BaseButton
                        v-if="isNewSearchArea"
                        :loading="loading"
                        :placeholder="$t('_common:buttons.searchThisArea')"
                        outlined
                        color="primary"
                        class="gst-hotels-map__load-more-button d-flex"
                        @click="searchNewArea" />
                    <BaseButton
                        v-else-if="!allDataLoaded"
                        :loading="loading"
                        :placeholder="$t('_common:buttons.loadMoreHotels')"
                        outlined
                        color="primary"
                        class="gst-hotels-map__load-more-button"
                        @click="loadNextPage" />
                </template>
            </div>
            <div
                v-if="showDifferentCountryNotification"
                class="gst-notification-card-wrapper mt-2">
                <NotificationCard
                    v-if="showOutsideCountryNotification"
                    type="warning"
                    :can-close="true"
                    class="gst-notification-card"
                    @close="hideOutsideCountryNotification">
                    {{ $t('_common:messages.movedOutsideEventCountry') }}
                </NotificationCard>
            </div>
            <div
                v-else-if="showPanAwayLimitNotification && !notificationHidden"
                class="mt-2 gst-notification-card-wrapper">
                <NotificationCard
                    v-if="showNotification"
                    type="warning"
                    :can-close="true"
                    @close="hideNotification">
                    {{ $t('_common:messages.tooFarFromEvent') }}
                </NotificationCard>
            </div>
        </template>
    </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import isEqual from 'lodash/isEqual';
import EventFlowMixin from '@tenant/app/mixins/EventFlowMixin';
import NotificationCard from '@tenant/app/components/notifications/NotificationCard.vue';

export default {
    name: 'HotelsMapActions',
    components: {
        NotificationCard,
    },
    mixins: [EventFlowMixin],
    props: {
        map: {
            type: Object,
            required: true,
        },
    },
    data: () => ({
        showNotification: true,
        notificationHidden: false,
        showOutsideCountryNotification: true,
        showDifferentCountryNotification: false,
    }),
    computed: {
        ...mapState({
            loadingHotels: (state) => state.addHotelReservationState.hotels.loading,
            allDataLoaded: (state) => state.addHotelReservationState.hotels.allItemsLoaded,
            filters: (state) => state.addHotelReservationState.filters,
            limit: (state) => state.addHotelReservationState.limit,
            location: (state) => state.addHotelReservationState.hotels.filters.location,
            initialLoad: (state) => state.addHotelReservationState.hotels.initialLoad,
            mapCenter: (state) => state.addHotelReservationState.map.mapCenter,
            searchCenter: (state) => state.addHotelReservationState.map.searchCenter,
            closestCityNotFound: (state) => state.addHotelReservationState.closestCityNotFound,
            itemsNotFound: (state) => state.addHotelReservationState.hotels.itemsNotFound,
        }),
        ...mapGetters({
            didHotelFiltersChangeFromContext:
                'addHotelReservationState/didHotelFiltersChangeFromContext',
            isNewSearchArea: 'addHotelReservationState/map/isNewSearchArea',
            isTooFarFromVenue: 'addHotelReservationState/map/isTooFarFromVenue',
            eventLocation: `events/getEventLocation`,
            loading: 'addHotelReservationState/loading',
        }),
        showReset() {
            return (
                this.isNewSearchArea || !this.initialLoad || this.didHotelFiltersChangeFromContext
            );
        },
        resultsNotFound() {
            return this.itemsNotFound || this.closestCityNotFound;
        },
        showPanAwayLimitNotification() {
            if (
                this.eventLocation?.latitude &&
                this.eventLocation?.longitude &&
                this.isDynamicFlow
            ) {
                return this.isTooFarFromVenue({
                    latitude: this.eventLocation.latitude,
                    longitude: this.eventLocation.longitude,
                });
            }
            return false;
        },
    },
    watch: {
        loadingHotels: {
            handler: function (newVal, oldVal) {
                if (!newVal && oldVal) {
                    const unwatchMapCenter = this.$watch(
                        'mapCenter',
                        () => {
                            if (this.map) {
                                const center = this.map.getCenter();

                                this.setSearchCenter({
                                    latitude: center.lat,
                                    longitude: center.lng,
                                });
                            }
                            unwatchMapCenter();
                        },
                        { deep: true }
                    );
                }
            },
        },
        showPanAwayLimitNotification(_, oldVal) {
            this.notificationHidden = oldVal;
        },
    },
    methods: {
        ...mapActions({
            loadPage: 'addHotelReservationState/hotels/loadPage',
            setInitialLoad: 'addHotelReservationState/hotels/setInitialLoad',
            updateFilters: 'addHotelReservationState/filters/update',
            resetFilters: 'addHotelReservationState/resetFilters',
            resetHotels: 'addHotelReservationState/resetHotels',
            setSearchCenter: 'addHotelReservationState/map/setSearchCenter',
            setMapCenter: 'addHotelReservationState/map/setMapCenter',
            updateFilterLocationWithMapCenter:
                'addHotelReservationState/updateFilterLocationWithMapCenter',
            findClosestCity: 'addHotelReservationState/findClosestCity',
            setItemsNotFound: 'addHotelReservationState/hotels/setItemsNotFound',
            setClosestCityNotFound: 'addHotelReservationState/setClosestCityNotFound',
        }),
        loadNextPage() {
            this.loadPage({
                filters: this.filters,
                limit: this.limit,
            });
            this.setInitialLoad(false);
        },
        onMapMoveEndDo() {
            const center = this.map.getCenter();

            this.setClosestCityNotFound(false);
            this.setItemsNotFound(false);
            this.setMapCenter({ latitude: center.lat, longitude: center.lng });
            this.didMoveBackToEventCountry();
        },
        async didMoveBackToEventCountry() {
            if (this.showDifferentCountryNotification) {
                const closestCity = await this.findClosestCity();
                this.showDifferentCountryNotification =
                    this.location.countryCode !== closestCity.countryCode;
            }
        },
        resetToInitialSearch() {
            this.resetHotels();
            this.resetFilters();
        },
        async searchNewArea() {
            const closestCity = await this.findClosestCity();

            if (
                !this.isOpenInventoryFlow &&
                closestCity &&
                this.location.countryCode !== closestCity.countryCode
            ) {
                this.showDifferentCountryNotification = true;
                this.showOutsideCountryNotification = true;
                return;
            }

            await this.updateFilterLocationWithMapCenter(closestCity);

            const query = {
                ...this.$route.query,
                ...this.filters.location,
            };

            if (!isEqual(query, this.$route.query)) {
                this.$router.replace({
                    ...this.$route,
                    query: query,
                });
            }
        },
        hideNotification() {
            this.notificationHidden = true;
        },
        hideOutsideCountryNotification() {
            this.showOutsideCountryNotification = false;
        },
    },
    mounted() {
        const center = this.map.getCenter();
        this.setMapCenter({ latitude: center.lat, longitude: center.lng });
        this.setSearchCenter({ latitude: center.lat, longitude: center.lng });
        this.map.on('moveend', this.onMapMoveEndDo);
    },
    destroyed() {
        this.map.off('moveend', this.onMapMoveEndDo);
    },
};
</script>

<style lang="scss" scoped>
@import '@scssVariables';
@import '@scssMixins';

.gst-hotels-map__load-more-button {
    background: white;
    box-shadow: 4px 6px 12px 0 rgba(0, 0, 0, 0.12) !important;
    text-transform: capitalize;
}

.gst-hotels-map__refresh-button {
    padding: theme-spacing(0) !important;
    border: 1px solid theme-color('primary-darken-2');
    margin-right: theme-spacing(2);
    background: theme-color('primary-darken-2');
    min-width: 38px !important;
}

.gst-notification-card {
    box-shadow: 0 12px 12px 0 rgba(0, 0, 0, 0.06);
    box-shadow: 0 0 12px 0 rgba(0, 0, 0, 0.12);
}

.gst-notification-card-wrapper {
    background: theme-color('white');
}

@include mobile-only {
    .gst-notification-card {
        min-width: 340px;
    }
}
</style>
